import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { Input } from "@/design-system/components/ui";
import {
  internalUsersApi,
  useInternalUserLabels,
  fallbackUserLabel,
  type InternalUser,
} from "@/api/internalUsers";

const DEBOUNCE_MS = 250;
const MAX_RESULTS = 10;

export interface InternalUserPickerProps {
  /** Array of selected internal-user ids. */
  value: string[];
  onChange: (userIds: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Optional cap on how many users can be selected. Unlimited by default. */
  maxSelections?: number;
  /**
   * Optional per-user warning labels keyed by user id. When an id is present,
   * its chip renders a small warning badge with the label as its tooltip (used
   * by the calendar to surface an attendee's scheduling conflict). Advisory
   * only — it never disables selection. Undefined by default, so consumers that
   * don't pass it are unaffected.
   */
  conflictLabels?: ReadonlyMap<string, string>;
}

/**
 * External Portal v2 PR 3 (PROB-17) — multi-select internal-user picker.
 * Patterned after the single-select UserPicker but holds an array of ids and
 * renders the current selection as removable chips. Search matches name /
 * email / role code via GET /api/internal/users/search; a raw GUID is never
 * shown. Used by the Manage-Assignees panel on the staging review surface.
 */
export function InternalUserPicker({
  value,
  onChange,
  placeholder = "Search by name, email, or role code…",
  disabled,
  maxSelections,
  conflictLabels,
}: InternalUserPickerProps) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState<InternalUser[]>([]);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastQueryRef = useRef<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve the selected ids to names for the chips. Search matches we've
  // already seen are merged in so a freshly-added chip shows its name even
  // before the labels query refetches.
  const labels = useInternalUserLabels(value);
  const matchById = useMemo(() => {
    const m = new Map<string, InternalUser>();
    for (const u of matches) m.set(u.id, u);
    return m;
  }, [matches]);

  const atCapacity =
    typeof maxSelections === "number" && value.length >= maxSelections;

  function scheduleSearch(query: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      lastQueryRef.current = query;
      setLoading(true);
      try {
        const results = await internalUsersApi.search(query, true);
        if (lastQueryRef.current === query) {
          setMatches(results.slice(0, MAX_RESULTS));
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const node = containerRef.current;
      if (node && !node.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function nameFor(id: string): string {
    const u = labels.lookup(id) ?? matchById.get(id);
    return u?.displayName ?? fallbackUserLabel(id);
  }

  function roleFor(id: string): string | null {
    const u = labels.lookup(id) ?? matchById.get(id);
    return u?.roleCode ?? null;
  }

  function addUser(id: string) {
    if (value.includes(id)) return;
    if (atCapacity) return;
    onChange([...value, id]);
    setText("");
    setOpen(false);
  }

  function removeUser(id: string) {
    onChange(value.filter((v) => v !== id));
  }

  // Hide already-selected users from the result list.
  const visibleMatches = matches.filter((m) => !value.includes(m.id));

  return (
    <div ref={containerRef} className="flex flex-col gap-space-2">
      <div className="flex flex-wrap items-center gap-space-1 border border-border-light rounded-md p-space-2 bg-white-pure min-h-10">
        {value.map((id) => {
          const role = roleFor(id);
          const conflictLabel = conflictLabels?.get(id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-space-1 px-space-2 py-[2px] bg-gold/15 text-onyx rounded-full text-caption font-display"
            >
              {nameFor(id)}
              {role && (
                <span className="text-[10px] uppercase tracking-wide text-gold-shadow">
                  {role}
                </span>
              )}
              {conflictLabel && (
                <span
                  title={conflictLabel}
                  aria-label={`Scheduling conflict: ${conflictLabel}`}
                  className="inline-flex items-center text-status-warning"
                >
                  <AlertTriangle size={12} strokeWidth={2} aria-hidden />
                </span>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeUser(id)}
                  aria-label={`Remove ${nameFor(id)}`}
                  className="text-gold-shadow hover:text-status-error"
                >
                  <X size={12} strokeWidth={2} aria-hidden />
                </button>
              )}
            </span>
          );
        })}
        <Input
          className="flex-1 min-w-[10rem] border-0 h-7 px-space-1 focus:shadow-none"
          type="text"
          value={text}
          disabled={disabled || atCapacity}
          placeholder={atCapacity ? "Maximum reached" : placeholder}
          aria-label="Search internal users"
          role="combobox"
          aria-expanded={open}
          onFocus={() => {
            setOpen(true);
            scheduleSearch("");
          }}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            scheduleSearch(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
            if (
              e.key === "Backspace" &&
              text === "" &&
              value.length > 0 &&
              !disabled
            ) {
              removeUser(value[value.length - 1]);
            }
          }}
        />
      </div>
      {open && !disabled && !atCapacity && (
        <div className="rounded-md border border-border-light bg-white-pure shadow-sm">
          {loading && visibleMatches.length === 0 && (
            <div className="px-space-3 py-space-2 text-label text-gray-mid">
              Searching…
            </div>
          )}
          {!loading && visibleMatches.length === 0 && (
            <div className="px-space-3 py-space-2 text-label text-gray-mid">
              {text.trim() === "" ? "Type to search" : "No matches"}
            </div>
          )}
          {visibleMatches.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => addUser(u.id)}
              className="flex w-full flex-col items-start px-space-3 py-space-2 text-left hover:bg-paper-warm"
            >
              <span className="text-small text-onyx">
                {u.displayName}
                {u.roleCode && (
                  <span className="ml-space-1 text-[10px] uppercase tracking-wide text-gold-shadow">
                    {u.roleCode}
                  </span>
                )}
              </span>
              {u.email && (
                <span className="text-caption text-gray-mid">{u.email}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
